import { getBizErrorInfo } from '@/requestErrorConfig';
import { login as loginRequest } from './generated/login';
import { request } from './request';

export interface LoginIdentity {
  type: 'account' | 'mobile';
  username?: string;
  mobile?: string;
}

export interface SlideChallenge {
  id: string;
  purpose: 'login';
  provider: 'go-captcha';
  expiresAt: string;
  width: number;
  height: number;
  image: string;
  thumb: string;
  thumbX: number;
  thumbY: number;
  thumbWidth: number;
  thumbHeight: number;
}

export async function getLoginChallenge(identity: LoginIdentity, signal?: AbortSignal) {
  const result = await request<{ success: true; data: SlideChallenge }>(
    '/api/login/challenge',
    {
      method: 'POST',
      data: { ...identity, purpose: 'login' },
      signal,
      skipErrorHandler: true,
    },
  );
  if (
    !result.data?.id ||
    result.data.provider !== 'go-captcha' ||
    !result.data.image ||
    !result.data.thumb ||
    result.data.width <= 0 ||
    !Number.isFinite(Date.parse(result.data.expiresAt))
  ) {
    throw new Error('Invalid challenge response');
  }
  return result.data;
}

export async function verifyLoginChallenge(
  identity: LoginIdentity,
  id: string,
  point: { x: number; y: number },
) {
  const result = await request<{
    success: true;
    data: { token: string; expiresAt: string };
  }>('/api/login/challenge/verify', {
    method: 'POST',
    data: { ...identity, purpose: 'login', id, ...point },
    skipErrorHandler: true,
  });
  if (!result.data?.token || !Number.isFinite(Date.parse(result.data.expiresAt))) {
    throw new Error('Invalid verification response');
  }
  return result.data;
}

// Additive Fake API fields; keep generated transport and backend contracts intact.
export type VerifiedLoginParams = API.LoginParams & { humanVerificationToken?: string };

export type LoginVerificationErrorCode =
  | 'CAPTCHA_REQUIRED'
  | 'CAPTCHA_INVALID'
  | 'CAPTCHA_EXPIRED'
  | 'CAPTCHA_USED'
  | 'CAPTCHA_CONTEXT'
  | 'CAPTCHA_PURPOSE'
  | 'CAPTCHA_RATE_LIMITED'
  | 'CAPTCHA_UNAVAILABLE'
  | 'ACCOUNT_LOCKED'
  | 'LOGIN_LOCKED'
  | 'UNKNOWN';

export function normalizeLoginVerificationErrorCode(
  value: unknown,
): LoginVerificationErrorCode {
  switch (value) {
    case 'CAPTCHA_REQUIRED':
    case 'CAPTCHA_INVALID':
    case 'CAPTCHA_EXPIRED':
    case 'CAPTCHA_USED':
    case 'CAPTCHA_CONTEXT':
    case 'CAPTCHA_PURPOSE':
    case 'CAPTCHA_RATE_LIMITED':
    case 'CAPTCHA_UNAVAILABLE':
    case 'ACCOUNT_LOCKED':
    case 'LOGIN_LOCKED':
      return value;
    default:
      return 'UNKNOWN';
  }
}

type LoginFailureDetails = {
  retryAfter?: number;
};

export type VerifiedLoginResult =
  | {
      status: 'ok';
      token?: string;
      type?: string;
      currentAuthority?: string;
    }
  | (LoginFailureDetails & {
      status: 'error';
      humanVerificationRequired: boolean;
      verificationError?: LoginVerificationErrorCode;
    })
  | (LoginFailureDetails & {
      status: 'verification_required';
      humanVerificationRequired: true;
      verificationError: LoginVerificationErrorCode;
    })
  | (LoginFailureDetails & {
      status: 'locked';
      humanVerificationRequired: false;
      verificationError: LoginVerificationErrorCode;
    });

type LoginResponse = Awaited<ReturnType<typeof loginRequest>> & {
  humanVerificationRequired?: unknown;
  verificationError?: unknown;
  retryAfter?: unknown;
};

function normalizeLoginResult(result: LoginResponse): VerifiedLoginResult {
  if (result.status === 'ok') {
    return {
      status: 'ok',
      token: result.token,
      type: result.type,
      currentAuthority: result.currentAuthority,
    };
  }

  const verificationError = normalizeLoginVerificationErrorCode(
    result.verificationError,
  );
  const retryAfter =
    typeof result.retryAfter === 'number' && Number.isFinite(result.retryAfter)
      ? result.retryAfter
      : undefined;
  if (
    result.status === 'locked' ||
    verificationError === 'ACCOUNT_LOCKED' ||
    verificationError === 'LOGIN_LOCKED'
  ) {
    return {
      status: 'locked',
      humanVerificationRequired: false,
      verificationError,
      retryAfter,
    };
  }
  if (result.status === 'verification_required') {
    return {
      status: 'verification_required',
      humanVerificationRequired: true,
      verificationError,
      retryAfter,
    };
  }
  return {
    status: 'error',
    humanVerificationRequired: result.humanVerificationRequired === true,
    verificationError:
      result.status === 'error' && result.verificationError === undefined
        ? undefined
        : verificationError,
    retryAfter,
  };
}

export async function submitVerifiedLogin(
  params: VerifiedLoginParams,
  signal?: AbortSignal,
): Promise<VerifiedLoginResult> {
  try {
    return normalizeLoginResult(
      await loginRequest(params, { skipErrorHandler: true, signal }),
    );
  } catch (error) {
    const info = getBizErrorInfo(error);
    if (!info || info.errorCode === undefined) {
      throw error;
    }
    const verificationError = normalizeLoginVerificationErrorCode(info.errorCode);
    return normalizeLoginResult({
      status:
        verificationError !== 'UNKNOWN' &&
        verificationError !== 'ACCOUNT_LOCKED' &&
        verificationError !== 'LOGIN_LOCKED'
          ? 'verification_required'
          : 'error',
      verificationError,
    });
  }
}
